<script setup lang="ts">
import { NCollapse, NCollapseItem } from 'naive-ui'
import { v4 as uuid } from 'uuid'
import { useI18n } from 'vue-i18n'
import { computed, ref, onMounted, inject, nextTick, type ComputedRef, type Ref } from 'vue'
import { dayjs } from '@lib/util/date'

import type { File as PFile, CollaborationRoleEnum, User, PaginatedMetadataTemplateList } from '@openapi'
import { AccessTypeEnum, MetadataTemplateFieldTypeEnum } from '@openapi'
import type { Upload, UploadStatus } from '@browser/stores/upload.types'
import useUploadStore from '@browser/stores/upload'
import useAppStore from '@browser/stores/app'
import useApiStore from '@browser/stores/api'
import CModal from '@lib/components/CModal/CModal.vue'
import CCard from '@lib/components/CCard/CCard.vue'
import UploadFilesModalFileList from './UploadFilesModalFileList.vue'
import UploadFilesModalButtons from './UploadFilesModalButtons.vue'
import UploadFilesModalPropertiesVue from './UploadFilesModalProperties.vue'
import UploadFilesModalInput from './UploadFilesModalInput.vue'
import { MAX_UPLOAD_SIZE } from '@lib/constants/uploads.const'
import type { DeviceFile } from '@lib/types/files'
import type { ConfigForm } from '@lib/components/CConfigEditor/CConfigEditor.types'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import CConfigEditor from '@lib/components/CConfigEditor/CConfigEditor.vue'
import type { GetInstanceMetadataTemplatesPayload } from '@sdk/payload.types'

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const { t } = useI18n()

const configEditorRef = ref<null | InstanceType<typeof CConfigEditor>>(null)
const metadata = ref<Record<string, any>>()
const metadataFetcher = inject<
  (params?: GetInstanceMetadataTemplatesPayload) => Promise<PaginatedMetadataTemplateList>
>('instanceMetadataTemplatesFetcher')
const instanceMetadataTemplate = ref<ConfigForm>()
const areMetadataValid = ref(true)

const defaultValues = ref<Record<string, any>>()
const convertPptxToHtml = ref<boolean>(pitcherSettings.value.convert_pptx_to_html_default_value ?? false)

function getFileExtension(file: File | DeviceFile): string {
  const fileName = file.name || ''
  const parts = fileName.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
}

const hasPptxFiles = computed(() => {
  return uploadSelectedFiles.value.some((file) => getFileExtension(file) === 'pptx')
})

const shouldShowConvertPptxToHtml = computed(() => {
  return pitcherSettings.value.convert_pptx_to_html && hasPptxFiles.value
})

const {
  state: { currentFolderId },
  actions: { onUploadFilesRefetchFolder },
} = useApiStore()

const {
  actions: { addUploads },
  state: { uploadSelectedFiles },
} = useUploadStore()

const { setCurrentState, appName } = useAppStore()

const fileInputRef: Ref<InstanceType<typeof UploadFilesModalInput> | null> = ref(null)

interface UserCollaboration {
  file_id: PFile['id']
  role: CollaborationRoleEnum
  user_id: User['id']
}

enum FileExpiration {
  Never = 'never',
  At = 'at',
  Expired = 'expired',
}

export interface Form {
  permissions: PFile['permissions']
  tags: PFile['tags']
  expiresAtDateTime: number | null
  expiresAtOption: FileExpiration
  accessType: PFile['access_type']
  collaborations: UserCollaboration[]
}

const form: Ref<Form> = ref({
  permissions: {
    can_share: true,
  },
  tags: [],
  expiresAtDateTime: pitcherSettings.value?.is_file_expiration_mandatory
    ? dayjs().add(7, 'day').endOf('day').valueOf()
    : null,
  expiresAtOption: pitcherSettings.value?.is_file_expiration_mandatory ? FileExpiration.At : FileExpiration.Never,
  accessType: appName.value === 'admin' ? AccessTypeEnum.PUBLIC : AccessTypeEnum.RESTRICTED,
  collaborations: [],
  metadata: {},
})

function onSelectFiles(selectedFiles: File[] | DeviceFile[]) {
  uploadSelectedFiles.value = [...uploadSelectedFiles.value, ...selectedFiles] as File[] | DeviceFile[]
}

async function validateMetadata() {
  if (configEditorRef.value) {
    const { success, config } = await configEditorRef.value.validate()
    areMetadataValid.value = success
    metadata.value = config
  }
}

async function validateMetadataField() {
  await validateMetadata()
}

async function validateMetadataForm() {
  await nextTick()
  await validateMetadata()
}

const isExpirationValid = computed(() => {
  const isMandatory = pitcherSettings.value?.is_file_expiration_mandatory
  if (isMandatory) {
    return form.value.expiresAtDateTime !== null && form.value.expiresAtDateTime !== undefined
  }
  return true
})

function onAddFiles() {
  fileInputRef.value?.onClick()
}

function cancel() {
  uploadSelectedFiles.value = []
  setCurrentState('browse')
}

function upload() {
  const uploadItems: Upload[] = uploadSelectedFiles.value.map((file) => {
    const isInLimits = file.size <= MAX_UPLOAD_SIZE
    const uploadStatus: UploadStatus = isInLimits ? 'pending' : 'failed'
    let error = null

    if (!isInLimits) {
      error = t('canvasUI.browserApp.uploadFilesModal.uploadQueue.errors.fileSizeExceeded', {
        size: MAX_UPLOAD_SIZE / 1024 ** 3, // convert to GB
      })
    }

    const isPptxFile = getFileExtension(file) === 'pptx'
    const fileMetadata = { ...metadata.value }

    if (isPptxFile && shouldShowConvertPptxToHtml.value) {
      fileMetadata.convert_pptx_to_html = convertPptxToHtml.value
    }

    return {
      ...('path' in file ? { path: file.path, content: file as DeviceFile } : { content: file as File }),
      id: uuid(),
      folder_id: currentFolderId,
      permissions: form.value.permissions,
      tags: form.value.tags,
      access_type: form.value.accessType,
      expires_at: form.value.expiresAtDateTime ? new Date(form.value.expiresAtDateTime).toISOString() : '',
      metadata: fileMetadata,
      isInLimits,
      uploadStatus,
      error,
      cancelErrorMessage: t('canvasUI.browserApp.uploadFilesModal.uploadQueue.errors.cancelled'),
      genericErrorMessage: t('canvasUI.browserApp.uploadFilesModal.uploadQueue.errors.generic'),
    }
  })

  addUploads(uploadItems, onUploadFilesRefetchFolder)

  setCurrentState('browse')
}

async function fetchMetadata() {
  if (launchDarkly.value.enable_metadata_in_file_uploads_with_default_values) {
    try {
      const response = await metadataFetcher?.()
      const results = response?.results ?? ([] as PaginatedMetadataTemplateList['results'])

      if (!results?.length) {
        console.warn('[UploadFilesModal]: No metadata templates found. Bailing...')
        return
      }

      const [t] = results
      instanceMetadataTemplate.value = t as ConfigForm

      defaultValues.value = Object.fromEntries(
        instanceMetadataTemplate.value.sections.flatMap((section) =>
          section.fields
            .filter((field) => field.default !== undefined)
            .map((field) => {
              let defaultValue = field.default
              switch (field.type) {
                case MetadataTemplateFieldTypeEnum.NUMBER:
                  defaultValue = Number(defaultValue) || null
                  break
                case MetadataTemplateFieldTypeEnum.DATE:
                  defaultValue = defaultValue || null
                  break
              }
              return [field.name, defaultValue]
            }),
        ),
      )

      await validateMetadataForm()
    } catch (e) {
      console.error('[UploadFilesModal]: Error fetching metadata templates', e)
    }
  }
}

onMounted(async () => {
  if (uploadSelectedFiles.value.length === 0) onAddFiles()

  await fetchMetadata()
})
</script>

<template>
  <CModal class="max-h-[calc(100vh-48px)]" :show="true">
    <CCard aria-modal="true" class="max-w-[640px] overflow-y-auto" role="dialog" size="medium">
      <div class="flex flex-col gap-4">
        <div class="text-lg leading-8 tracking-wide">
          {{ t('canvasUI.browserApp.uploadFilesModal.title') }}
        </div>

        <div class="max-h-[calc(100vh-200px)] overflow-y-auto">
          <UploadFilesModalFileList v-model="uploadSelectedFiles" @add-files="onAddFiles" />

          <NCollapse
            :default-expanded-names="['properties', 'metadata']"
            display-directive="show"
            :on-item-header-click="() => validateMetadataForm()"
          >
            <NCollapseItem
              arrow-placement="left"
              name="properties"
              :title="t('canvasUI.browserApp.uploadFilesModal.properties')"
            >
              <UploadFilesModalPropertiesVue
                v-model="form"
                :convert-pptx-to-html="convertPptxToHtml"
                :show-convert-pptx-to-html="shouldShowConvertPptxToHtml"
                @update:convert-pptx-to-html="(val) => (convertPptxToHtml = val)"
              />
            </NCollapseItem>
            <NCollapseItem
              v-if="launchDarkly.enable_metadata_in_file_uploads_with_default_values"
              arrow-placement="left"
              name="metadata"
              :title="t('canvasUI.browserApp.uploadFilesModal.metadata')"
            >
              <CConfigEditor
                v-if="instanceMetadataTemplate"
                ref="configEditorRef"
                :initialValues="defaultValues"
                :template="instanceMetadataTemplate"
                @change="validateMetadataField"
              />
            </NCollapseItem>
          </NCollapse>
        </div>
        <UploadFilesModalButtons
          :disableDone="!areMetadataValid || uploadSelectedFiles.length === 0 || !isExpirationValid"
          @cancel="cancel"
          @upload="upload"
        />
      </div>
    </CCard>
  </CModal>
  <UploadFilesModalInput ref="fileInputRef" @select-files="onSelectFiles" />
</template>
